// src/components/Login.js
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../commonLogin.css";
import Toast from "../../utils/toast";
import Loader from "../Loader";
import createInstance from "../../api/axiosinstance";
import { useDispatch, useSelector } from "react-redux";
import {
  loginSuccess,
  setLoading,
  setError,
  logout,
  loginAdmin,
  loginOrgUser,
  checkUserType,
  requestCandidateOtp,
  getAllTPInterviewers,
  loginTPUser,
} from "../../Redux/slices/authSlice";
import {
  Button,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  Fade,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";

const Login = () => {
  const dispatch = useDispatch();
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const { isAuthenticated, loading, error, userType, user } = useSelector(
    (state) => state.auth
  );
  const passwordRef = useRef(null);
  const [formData, setFormData] = useState({ email: "" });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    role: "",
    organization: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dishaApi = createInstance("DISHA");
  const [step, setStep] = useState("email");

  useEffect(() => {
    if (isAuthenticated && user) {
      const route = user.role?.toLowerCase() + "Landing";
      navigate("/" + route);
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateEmail = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setErrors({ ...errors, email: "Please enter a valid email address" });
      return false;
    }
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    dispatch(setLoading(true));
    try {
      const result = await dispatch(checkUserType(formData.email)).unwrap();
      if (result.role === "candidate") {
        // Candidate: Request OTP
        await dispatch(requestCandidateOtp(formData.email)).unwrap();
        navigate("/otpVerification", {
          state: { email: formData.email, source: "candidateLogin" },
        });
        Toast.success("OTP sent successfully");
      } else {
        // Admin, OrgAdmin, Interviewer: Show password field
        setStep("password");
        Toast.info("Please enter your password");
      }
    } catch (err) {
      Toast.error(err || "User not found");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const passwordValue = passwordRef.current?.value || "";
    if (!passwordValue) {
      setErrors({ ...errors, password: "Please enter a password" });
      return;
    }

    dispatch(setLoading(true));
    try {
      let userData;

      if (userType === "Admin") {
        userData = await dispatch(
          loginAdmin({ email: formData.email, password: passwordValue })
        ).unwrap();
      } else if (["OrgAdmin", "Interviewer", "HR"].includes(userType)) {
        userData = await dispatch(
          loginOrgUser({ email: formData.email, password: passwordValue })
        ).unwrap();
      } else if (userType === "TP-Interviewer") {
        // ✅ Step 1: Get all interviewers
        const allInterviewers = await dispatch(getAllTPInterviewers()).unwrap();
        const matchedUser = allInterviewers.find(
          (u) => u.email === formData.email
        );

        if (matchedUser && matchedUser.activation_status === "Pending") {
          // ✅ Step 2: Show popup and stop login
          setShowVerificationPopup(true);
          dispatch(setLoading(false));
          return;
        }

        // ✅ Step 3: Proceed to login if active
        userData = await dispatch(
          loginTPUser({ email: formData.email, password: passwordValue })
        ).unwrap();
      }

      // 🔒 Check activation status after login
      if (userData?.activation_status === "Pending") {
        setShowVerificationPopup(true);
        dispatch(setLoading(false));
        return;
      }

      if (userData?.status === "Active") {
        navigate(`/${userData.role.toLowerCase()}Landing`);
        console.log("dddd",userData.role)
      } else {
        navigate("/changePassword");
      }

      Toast.success("Logged in successfully");
    } catch (err) {
      Toast.error("Invalid credentials");
      console.log("errrr",err)
    } finally {
      dispatch(setLoading(false));
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const goBack = () => {
    setStep("email");
    setErrors({ ...errors, password: "" });
  };

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Box sx={{ width: "100%", maxWidth: "400px", padding: "20px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                backgroundColor: "rgba(0, 106, 113, 0.8)",
                mb: 2,
              }}
            >
              <i className="bi bi-person-circle" style={{ fontSize: 30 }}></i>
            </Avatar>
            <Typography
              variant="h4"
              className="gradient-text"
              sx={{
                fontWeight: 700,
                backgroundImage: "linear-gradient(135deg, #00e0ff, #00a2a2)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 2px 5px rgba(0,0,0,0.2)",
              }}
            >
              {step === "email" ? "Sign In" : "Enter Password"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                mt: 1,
                textAlign: "center",
              }}
            >
              {step === "email"
                ? "Enter your email to continue to Strategic Hire"
                : `Welcome back, let's verify it's you`}
            </Typography>
          </Box>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
            onSubmit={
              step === "email" ? handleEmailSubmit : handlePasswordSubmit
            }
          >
            {step === "email" ? (
              <TextField
                type="email"
                name="email"
                placeholder="Email Address"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                size="medium"
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i
                        className="bi bi-envelope"
                        style={{ color: "#00a2a2" }}
                      ></i>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "rgba(0, 106, 113, 0.1)",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a2a2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00a2a2",
                      borderWidth: "2px",
                    },
                    "& .MuiInputBase-input": {
                      color: "white", // This makes the input text white
                    },
                  },
                }}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgba(0, 106, 113, 0.1)",
                    borderRadius: "12px",
                    mb: 2,
                    cursor: "pointer",
                  }}
                  onClick={goBack}
                >
                  <i
                    className="bi bi-envelope"
                    style={{ color: "#00a2a2", marginRight: "10px" }}
                  ></i>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                    >
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ color: "white" }}>
                      {formData.email}
                    </Typography>
                  </Box>
                  <i className="bi bi-pencil" style={{ color: "#00a2a2" }}></i>
                </Box>

                <TextField
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  inputRef={passwordRef}
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="medium"
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <i
                          className="bi bi-lock"
                          style={{ color: "#00a2a2" }}
                        ></i>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#00a2a2" }}
                        >
                          {showPassword ? (
                            <i className="bi bi-eye"></i>
                          ) : (
                            <i className="bi bi-eye-slash"></i>
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "rgba(0, 106, 113, 0.1)",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a2a2",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00a2a2",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "white", // This makes the input text white
                    },
                    "& .MuiFormHelperText-root": {
                      color: "rgba(255, 255, 255, 0.7)", // This makes helper text light white
                    },
                  }}
                />
              </>
            )}

            {step === "password" && (
              <Box sx={{ textAlign: "right", mt: -2 }}>
                <Link to="/forgotPassword" className="text-decoration-none">
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#00e0ff",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                borderRadius: "12px",
                padding: "12px",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: "600",
                letterSpacing: "0.5px",
                background: "linear-gradient(135deg, #00e0ff, #00a2a2)",
                boxShadow: "0 4px 10px rgba(0, 162, 162, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #00d1ee, #009191)",
                  boxShadow: "0 6px 15px rgba(0, 162, 162, 0.4)",
                },
                mt: 2,
              }}
            >
              {loading ? (
                <Loader />
              ) : (
                <>
                  {step === "email" ? "Continue" : "Sign In"}
                  <i
                    className="bi bi-arrow-right"
                    style={{ marginLeft: "8px" }}
                  ></i>
                </>
              )}
            </Button>
          </Box>

          {error && (
            <Typography
              variant="body2"
              sx={{
                color: "#ff5252",
                mt: 2,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="bi bi-exclamation-circle"
                style={{ marginRight: "6px" }}
              ></i>
              {error}
            </Typography>
          )}

          {/* <Box sx={{ mt: 4, textAlign: "center" }}>
          <Divider sx={{ 
            "&::before, &::after": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            }
          }}>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
              New to Strategic Hire?
            </Typography>
          </Divider>
          
          <Link to="/register" className="text-decoration-none">
            <Button 
              fullWidth
              sx={{
                mt: 2,
                borderRadius: "12px",
                padding: "10px",
                textTransform: "none",
                fontSize: "1rem",
                border: "2px solid rgba(0, 224, 255, 0.3)",
                color: "#00e0ff",
                "&:hover": {
                  backgroundColor: "rgba(0, 224, 255, 0.1)",
                  border: "2px solid rgba(0, 224, 255, 0.6)",
                }
              }}
            >
              Create an Account
            </Button>
          </Link>
        </Box> */}
        </Box>
        {/* Verification Popup */}
        <Dialog
          open={showVerificationPopup}
          onClose={() => setShowVerificationPopup(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: "rgba(0, 106, 113, 0.95)",
              color: "white",
              padding: 2,
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 700,
              backgroundImage: "linear-gradient(135deg, #00e0ff, #00a2a2)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Account Under Verification
          </DialogTitle>

          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "rgba(255,255,255,0.85)",
                mt: 1,
              }}
            >
              Your account is currently under verification. Please come back
              later once it’s approved.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => {
                setShowVerificationPopup(false);
                navigate("/");
              }}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                background: "linear-gradient(135deg, #00e0ff, #00a2a2)",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #00d1ee, #009191)",
                },
              }}
            >
              Home
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default Login;
